/**
 * @file api.ts
 * @brief TanStack Query hooks for B25's read-only Super Admin business views (W22).
 *
 * @details
 * `GET /api/admin/analytics/business/:view?from&to&tz&groupBy` returns the
 * payload bare (no `{ success, data }` envelope). Read-only: nothing here
 * writes. The server caches each view for 5 minutes; the client keeps a
 * result for the same 5 minutes so switching tabs does not refetch.
 *
 * DEV: `__setBusinessAnalyticsStub` answers every view in the page (the
 * preview at /__preview/business-analytics), so no backend is needed.
 *
 * @references BACKEND/src/features/admin/routes.js (/analytics/business/:view),
 *   docs/product/super-admin-analytics.md
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import type { BusinessQueryWindow } from "./businessMath";
import type { BusinessView, Costs, Margin, Overview, Saas, SubscribersByPrice } from "./types";

type Stub = (view: BusinessView, window: BusinessQueryWindow | null) => Promise<unknown>;
let stub: Stub | null = null;

/** DEV only: answer every view in the page. */
export function __setBusinessAnalyticsStub(next: Stub | null) {
  if (import.meta.env.DEV) stub = next;
}

function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

async function fetchView<T>(view: BusinessView, window: BusinessQueryWindow | null): Promise<T> {
  if (stub) return (await stub(view, window)) as T;
  const usp = new URLSearchParams();
  if (window) {
    usp.set("from", window.from);
    usp.set("to", window.to);
    usp.set("groupBy", window.groupBy);
    usp.set("tz", viewerTimeZone());
  }
  const qs = usp.toString();
  return apiGet<T>(`${adminEndpoints.analyticsBusiness}/${view}${qs ? `?${qs}` : ""}`);
}

const STALE_MS = 5 * 60 * 1000;

export const businessKeys = {
  view: (view: BusinessView, window: BusinessQueryWindow | null) =>
    ["admin", "analytics", "business", view, window] as const,
};

function useView<T>(view: BusinessView, window: BusinessQueryWindow | null, enabled = true) {
  // subscribers-by-price is the live state: the window does not change it.
  const w = view === "subscribers-by-price" ? null : window;
  return useQuery({
    queryKey: businessKeys.view(view, w),
    queryFn: () => fetchView<T>(view, w),
    enabled: enabled && (view === "subscribers-by-price" || w !== null),
    staleTime: STALE_MS,
    retry: 1,
  });
}

export const useBusinessOverview = (w: BusinessQueryWindow | null) => useView<Overview>("overview", w);
export const useBusinessMargin = (w: BusinessQueryWindow | null, enabled = true) =>
  useView<Margin>("margin", w, enabled);
export const useBusinessCosts = (w: BusinessQueryWindow | null, enabled = true) =>
  useView<Costs>("costs", w, enabled);
export const useBusinessSaas = (w: BusinessQueryWindow | null, enabled = true) =>
  useView<Saas>("saas", w, enabled);
export const useSubscribersByPrice = (enabled = true) =>
  useView<SubscribersByPrice>("subscribers-by-price", null, enabled);
